// Texture quality manager (ENG-757). Module-level singleton — NOT React state
// (CLAUDE.md hard rule): the cascade in avatarMaterial.js and the AvatarModel
// load path read it directly; UI surfaces subscribe for change ticks.
//
// Two distinct things live here:
//   • the user's SETTING — 'auto' or a forced tier, persisted per browser in
//     localStorage (quality is a device property, not an account property);
//   • the AUTO DECISION — recomputed every session from the GPU cap and the
//     bandwidth measured off the low-tier pass's own downloads. The last
//     measurement persists as a HINT (starting prior only — always overwritten
//     by the next real measurement, so it can never go stale-authoritative).

export const TIER_ORDER = ['low', 'medium', 'high', 'ultra']

export const SETTING_KEY = 'aos-texture-quality'
export const HINT_KEY = 'aos-texture-quality-hint'
export const OVERRIDES_KEY = 'aos-texture-quality-overrides'

// ── Pure decision functions (unit-tested directly) ─────────────────────────

// minTier returns the weaker of two tiers.
export function minTier(a, b) {
  return TIER_ORDER[Math.min(TIER_ORDER.indexOf(a), TIER_ORDER.indexOf(b))]
}

// gpuCapTier derives the ceiling this device should ever be asked to decode
// and hold in VRAM. Missing signals are permissive — the bandwidth term still
// gates the final pick, and a manual setting bypasses everything anyway.
export function gpuCapTier({ maxTextureSize, deviceMemoryGB, rendererString } = {}) {
  const r = (rendererString || '').toLowerCase()
  if (/swiftshader|llvmpipe|software/.test(r)) return 'low'
  if (Number.isFinite(maxTextureSize) && maxTextureSize < 4096) return 'low'
  if (Number.isFinite(deviceMemoryGB) && deviceMemoryGB <= 4) return 'medium'
  if (/mali-4|adreno \(tm\) [1-4]/.test(r)) return 'medium'
  if (Number.isFinite(maxTextureSize) && maxTextureSize < 8192) return 'high'
  return 'ultra'
}

// bandwidthTier maps a throughput measurement to the tier it can serve
// comfortably (Myra-scale wardrobe: ~1-2 MB at low, tens of MB at ultra).
export function bandwidthTier(mbps) {
  if (mbps < 2) return 'low'
  if (mbps < 8) return 'medium'
  if (mbps < 25) return 'high'
  return 'ultra'
}

// decideAutoTier — the Auto pick: min(gpu cap, bandwidth tier). With no
// bandwidth signal at all (cold start, no hint, no connection API) stay
// conservative at medium; the pass-1 measurement upgrades within seconds.
export function decideAutoTier({ cap, mbps }) {
  if (mbps == null || !Number.isFinite(mbps)) return minTier(cap, 'medium')
  return minTier(cap, bandwidthTier(mbps))
}

// A stored bandwidth measurement goes stale: networks change (office ↔ home ↔
// travel), and on a warm cache the pass-1 textures never hit the wire, so no
// fresh measurement ever overwrites a wrong prior — observed: a stored
// 845.6 Mbps hint forcing ultra textures on a link measuring ~1.6 Mbps, for
// session after session. A week-old prior is not a prior.
export const HINT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

// parseHint validates a stored measurement hint. Returns null on anything
// malformed OR expired — a bad hint must never poison the decision. `now` is
// injectable for tests; production callers omit it.
export function parseHint(raw, now = Date.now()) {
  if (!raw) return null
  try {
    const h = JSON.parse(raw)
    if (!h || typeof h !== 'object') return null
    if (!Number.isFinite(h.measuredMbps) || h.measuredMbps <= 0) return null
    if (!Number.isFinite(h.at) || now - h.at > HINT_MAX_AGE_MS) return null
    return { measuredMbps: h.measuredMbps, deviceCapTier: h.deviceCapTier, at: h.at }
  } catch {
    return null
  }
}

// parseOverrides validates a stored per-texture override map. Junk entries
// (unknown tiers, non-string values) are dropped, never trusted.
export function parseOverrides(raw) {
  if (!raw) return {}
  try {
    const m = JSON.parse(raw)
    if (!m || typeof m !== 'object' || Array.isArray(m)) return {}
    const out = {}
    for (const [id, tier] of Object.entries(m)) {
      if (TIER_ORDER.includes(tier)) out[id] = tier
    }
    return out
  } catch {
    return {}
  }
}

// ── Singleton state ─────────────────────────────────────────────────────────

const _state = {
  setting: 'auto',      // 'auto' | tier — the user's persisted choice
  deviceCap: 'ultra',   // gpu ceiling for this session
  measuredMbps: null,   // live measurement (pass-1 downloads); hint-seeded
  measuredIsHint: true, // whether measuredMbps is still just the stored prior
  activeTier: null,     // what the avatar is actually showing right now
  initialized: false,
  // Per-texture tier pins (ENG-757 follow-up). LOCAL to this browser by
  // design: two people co-editing the same character can each pick which
  // textures their machine loads at which quality. { textureId: tier }.
  overrides: null,          // lazily loaded from OVERRIDES_KEY
  overridesVersion: 0,      // bumped on change — AvatarModel re-targets on it
  // Live progress of the current upgrade pass (drives the viewport pill +
  // LiveTunePanel row). Zero-total upgrades never activate.
  upgrade: { active: false, done: 0, total: 0 },
}
const _listeners = new Set()

function _storage() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null } catch { return null }
}

function _notify() {
  for (const fn of _listeners) {
    try { fn() } catch (e) { console.warn('[textureQuality] listener failed:', e) }
  }
}

// initTextureQuality reads the device signals once. Called by AvatarModel with
// the R3F renderer, next to initKTX2. Idempotent.
export function initTextureQuality(renderer) {
  if (_state.initialized || !renderer) return
  _state.initialized = true

  let rendererString = ''
  try {
    const gl = renderer.getContext()
    const ext = gl.getExtension('WEBGL_debug_renderer_info')
    if (ext) rendererString = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || ''
  } catch { /* unmasked renderer unavailable — cap stays signal-driven */ }

  const nav = typeof navigator !== 'undefined' ? navigator : {}
  _state.deviceCap = gpuCapTier({
    maxTextureSize: renderer.capabilities?.maxTextureSize,
    deviceMemoryGB: nav.deviceMemory,
    rendererString,
  })

  const store = _storage()
  const stored = store?.getItem(SETTING_KEY)
  if (stored === 'auto' || TIER_ORDER.includes(stored)) _state.setting = stored

  // Bandwidth prior: last session's measurement beats the connection API's
  // guess; both are replaced by the first real pass-1 measurement.
  const hint = parseHint(store?.getItem(HINT_KEY))
  if (hint) {
    _state.measuredMbps = hint.measuredMbps
  } else if (Number.isFinite(nav.connection?.downlink) && nav.connection.downlink > 0) {
    _state.measuredMbps = nav.connection.downlink
  }
  _state.measuredIsHint = true

  console.log(
    `[textureQuality] cap=${_state.deviceCap} prior=${_state.measuredMbps ?? 'none'}Mbps ` +
    `setting=${_state.setting} → target=${getTargetTier()}`
  )
  _installDebugGlobal()
  _notify()
}

// A sample must be big enough and long enough to be a throughput measurement
// at all. Sub-threshold samples are dominated by things that aren't the
// network — request overhead, memory-cache serves, timing granularity — and a
// burst of tiny cached responses measures fiction (hundreds of Mbps on a link
// doing ~1.6 Mbps real). The ceiling is a plausibility bound: nothing this app
// serves arrives faster than 10 Gbps; a rate above it is a measurement
// artifact, not a network.
export const MIN_SAMPLE_MS = 250
export const MIN_SAMPLE_BYTES = 100_000
export const MAX_PLAUSIBLE_MBPS = 10_000

// recordDownloadSample feeds a real throughput observation (the low-tier
// pass's own downloads — no synthetic speed test). Overwrites the hint.
// Returns whether the sample was accepted, so callers/tests can tell a
// recorded measurement from a rejected artifact.
export function recordDownloadSample(bytes, ms) {
  if (!Number.isFinite(bytes) || !Number.isFinite(ms) || bytes <= 0 || ms <= 0) return false
  if (ms < MIN_SAMPLE_MS || bytes < MIN_SAMPLE_BYTES) return false
  const mbps = (bytes * 8) / (ms * 1000) // bytes/ms → megabits/s
  if (mbps > MAX_PLAUSIBLE_MBPS) return false
  _state.measuredMbps = mbps
  _state.measuredIsHint = false
  const store = _storage()
  try {
    store?.setItem(HINT_KEY, JSON.stringify({
      measuredMbps: Math.round(mbps * 10) / 10,
      deviceCapTier: _state.deviceCap,
      at: Date.now(),
    }))
  } catch { /* quota — hint is best-effort */ }
  _notify()
  return true
}

// recordFromResourceTimings measures throughput off the browser's resource
// timing entries for a set of just-downloaded URLs (the pass-1 texture
// fetches). Sums transfer bytes and spans first-request-start to
// last-response-end so parallel downloads aren't double-counted. Returns
// whether a sample was recorded.
export function recordFromResourceTimings(urls) {
  if (typeof performance === 'undefined' || !urls || !urls.length) return false
  const abs = new Set(urls.map((u) => {
    try { return new URL(u, location.href).href } catch { return u }
  }))
  let bytes = 0
  let start = Infinity
  let end = 0
  for (const e of performance.getEntriesByType('resource')) {
    if (!abs.has(e.name)) continue
    bytes += e.transferSize || e.encodedBodySize || 0
    start = Math.min(start, e.startTime)
    end = Math.max(end, e.responseEnd)
  }
  if (bytes <= 0 || end <= start) return false
  return recordDownloadSample(bytes, end - start)
}

export function getSetting() { return _state.setting }

// setSetting validates + persists the user's choice and notifies subscribers
// (AvatarModel re-targets the textures live). Returns false on junk input.
export function setSetting(s) {
  if (s !== 'auto' && !TIER_ORDER.includes(s)) return false
  if (s === _state.setting) return true
  _state.setting = s
  try { _storage()?.setItem(SETTING_KEY, s) } catch { /* best-effort */ }
  _notify()
  return true
}

export function getAutoTier() {
  return decideAutoTier({ cap: _state.deviceCap, mbps: _state.measuredMbps })
}

// getTargetTier — what the avatar should be showing: the forced tier, or the
// auto decision.
export function getTargetTier() {
  return _state.setting === 'auto' ? getAutoTier() : _state.setting
}

// setActiveTier is reported by the load/upgrade path so UI and tests can see
// what is actually on screen (vs what is targeted).
export function setActiveTier(tier) {
  if (_state.activeTier === tier) return
  _state.activeTier = tier
  _notify()
}

export function getActiveTier() { return _state.activeTier }

// ── Per-texture overrides ───────────────────────────────────────────────────

function _ensureOverrides() {
  if (_state.overrides === null) {
    _state.overrides = parseOverrides(_storage()?.getItem(OVERRIDES_KEY))
  }
  return _state.overrides
}

// getTextureOverride — the pinned tier for one texture, or null (follows the
// global setting).
export function getTextureOverride(textureId) {
  return _ensureOverrides()[textureId] ?? null
}

export function getOverrides() { return { ..._ensureOverrides() } }

export function getOverridesVersion() { return _state.overridesVersion }

// setTextureOverride pins one texture to a tier on THIS browser (null clears
// the pin). Persists + notifies; AvatarModel's subscription re-targets the
// loaded textures. Returns false on junk input.
export function setTextureOverride(textureId, tier) {
  if (!textureId) return false
  if (tier !== null && !TIER_ORDER.includes(tier)) return false
  const o = _ensureOverrides()
  if (tier === null) {
    if (!(textureId in o)) return true
    delete o[textureId]
  } else {
    if (o[textureId] === tier) return true
    o[textureId] = tier
  }
  _state.overridesVersion++
  try { _storage()?.setItem(OVERRIDES_KEY, JSON.stringify(o)) } catch { /* best-effort */ }
  _notify()
  return true
}

// ── Upgrade progress (drives the viewport pill) ─────────────────────────────

export function getUpgradeProgress() { return { ..._state.upgrade } }

export function beginUpgradeProgress(total) {
  if (!Number.isFinite(total) || total <= 0) return
  _state.upgrade = { active: true, done: 0, total }
  _notify()
}

export function tickUpgradeProgress() {
  if (!_state.upgrade.active) return
  _state.upgrade = { ..._state.upgrade, done: _state.upgrade.done + 1 }
  _notify()
}

export function endUpgradeProgress() {
  if (!_state.upgrade.active && _state.upgrade.total === 0) return
  _state.upgrade = { active: false, done: 0, total: 0 }
  _notify()
}

export function subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// ── Debug global (tests + LiveTunePanel readout) ────────────────────────────

function _installDebugGlobal() {
  if (typeof window === 'undefined') return
  window.__TEXTURE_QUALITY__ = {
    get setting() { return _state.setting },
    get autoTier() { return getAutoTier() },
    get deviceCap() { return _state.deviceCap },
    get measuredMbps() { return _state.measuredMbps },
    get measuredIsHint() { return _state.measuredIsHint },
    get activeTier() { return _state.activeTier },
    get upgrade() { return getUpgradeProgress() },
    get overrides() { return getOverrides() },
    setSetting,
    setTextureOverride,
  }
}
